import { debounce } from 'lodash';
import { getFirebase } from '@/domain/services/Rt/Firebase';
import { ignoreRepeatedCalls } from '@/domain/utils/Cache';
import { promiseQueue } from '@/domain/utils/Queue';
import { selectiveEncrypter } from '@/domain/services/Encrypter';
import { useRtConfig } from '@/domain/services/Rt/Config';
import { vueData } from '@/domain/services/VueData';
import { cacheDataSource, Callback, Collections, getCachedDataSource, Value } from '@/domain/services/Rt/DataSource';
import { doc, getDoc, onSnapshot, runTransaction, Timestamp } from 'firebase/firestore';

const subscriberFactory = <T>() => {
	const callbacks: Callback<T>[] = [];
	let callbackId = 0;

	return {
		subscribe: (callback: Callback<T>, ignoreRepetitions = false) => {
			callbackId++;
			const newId = callbackId;
			callbacks[newId] = ignoreRepetitions ? ignoreRepeatedCalls(callback) : callback;
			return () => {
				delete callbacks[newId];
			};
		},
		callAll: (data: T | null) => {
			Object.values(callbacks).forEach((callback) => callback(data));
		},
		removeAll: () => {
			Object.keys(callbacks).forEach((key) => delete callbacks[parseInt(key)]);
		},
	};
};

type DataMapper<T, R> = {
	extractData?: (data: T | null) => R | null;
	prepareData?: (data: T, updated: R) => T;
	guard?: (data: T) => boolean;
	rawProps?: (keyof T)[];
};

const dataSourceFactory = <T, R = unknown>(
	dataSourceId: string,
	collection: Collections,
	documentId: string,
	strategy: DataMapper<T, R> = {},
	nullableRecords = true,
	reinitialize = false
) => {
	if (!reinitialize) {
		const cachedDataSource = getCachedDataSource(dataSourceId);
		if (cachedDataSource) {
			return cachedDataSource;
		}
	}

	const { getFirestore, getEncrypter, isReadonly } = getFirebase();

	const db = getFirestore();
	const docRef = doc(db, `${collection}/${documentId}`);

	const { encrypt, decrypt } = selectiveEncrypter<T>(getEncrypter(), strategy.rawProps || []);

	const extract = (data: T | null) => (data && strategy.extractData ? strategy.extractData(data) : data);

	const prepare = (data: T | null, updated: Value<T | null>) => {
		const callback = updated ? updated?.callback : undefined;
		const dt = updated ? updated?.data || updated : null;
		return {
			newData: strategy.prepareData ? strategy.prepareData(data || ({} as T), dt as R) : dt,
			callback,
		};
	};

	const guard = strategy.guard || (() => true);

	const withTTL = (data: any) => ({
		data: data.data,
		expireAt: Timestamp.fromDate(new Date(vueData.variables?.firebaseTTL as Date)),
	});

	const { callAll, removeAll, subscribe } = subscriberFactory<T>();
	const removeListener = onSnapshot(docRef, (docSnap) => {
		if (docSnap.exists()) {
			const snap = docSnap.data()?.data || null;
			const decryptedRecord = snap !== null ? decrypt(snap) : null;
			const extractedData = extract(decryptedRecord as T);

			if (extractedData !== null) {
				callAll(extractedData as T);
				return;
			}
		}

		if (nullableRecords) {
			callAll(null);
		}
	});

	const sendTransaction = (transactionOrData: Value<T | null>) =>
		runTransaction(db, async (transaction) => {
			const docSnap = await transaction.get(docRef);
			if (docSnap.exists()) {
				const decryptedRecord = decrypt(docSnap.data()?.data);
				if (!guard(decryptedRecord as T)) {
					return Promise.reject(new Error('Record is locked'));
				}

				const { newData, callback } = prepare(decryptedRecord, transactionOrData);

				await transaction.update(docRef, withTTL({ data: encrypt(newData as T) } as T) || null);
				if (callback) callback();
			} else {
				const { newData, callback } = prepare({} as T, transactionOrData);
				await transaction.set(docRef, withTTL({ data: encrypt(newData as T) } as T) || null);
				if (callback) callback();
			}
		});

	const transactionQueue = promiseQueue<Value<T | null>>(sendTransaction);

	const { firebase } = useRtConfig();

	return cacheDataSource(dataSourceId, {
		id: dataSourceId,
		set: debounce(async (transactionOrData: Value<T | null>, forceSet = false) => {
			if (!forceSet && isReadonly()) {
				return;
			}

			transactionQueue.push(transactionOrData);
		}, firebase?.throttleTime || 400),
		get: async () => {
			const docSnap = await getDoc(docRef);
			const decryptedRecord = decrypt(docSnap.data()?.data);
			const extractedData = extract(decryptedRecord);
			return extractedData;
		},
		subscribe,
		destroy: () => {
			removeListener();
			removeAll();
		},
		remove: () => {},
		isReadonly,
	});
};

export { dataSourceFactory, DataMapper };
